import { Container, Heading, Loader, NoData, Select, CountUp, TaskTooltipContent } from "@/components"
import useViewTaskPlan from "./useViewTaskPlan";
import { cn, taskLegend, taskStatus, getHoverCardColors } from "@/lib/utils";
import { Button, HoverCard, HoverCardContent, HoverCardTrigger, Popover, PopoverContent, PopoverTrigger, TooltipProvider } from "@/components/ui";
import { ArrowLeft } from "lucide-react";

const ViewTaskPlan = () => {
  const {
    breadcrumbData,
    isLoadingTaskList,
    taskList,
    groupedTasks,
    filterType,
    setFilterType,
    onBackClick,
    onTaskClick,
    taskSummary,
    isMobile,
  } = useViewTaskPlan();
  
  return (
    <Container>
      {/* Header & Search */}
      <div className="flex flex-col lg:flex-row gap-4 lg:items-center lg:justify-between mb-4">
        <div className="flex flex-row items-center justify-between">
          <Heading title={breadcrumbData[breadcrumbData.length - 1]?.name || ''} breadcrumbData={breadcrumbData} />
        </div>
        <div className="flex flex-col md:flex-row md:items-center gap-3">
          <div className="w-[140px]">
            <Select 
              options={[{label: 'By Month', value: 'month'}, {label: 'By Week', value: 'week'}]}
              value={filterType}
              onChange={(val) => setFilterType(val)}
              className="bg-white"
            />
          </div>
          <Button className="bg-white hover:bg-white border-primary border-1" onClick={onBackClick}>
            <ArrowLeft /> Back to Gantt Chart
          </Button>
        </div>
      </div>



      <div className="flex flex-wrap gap-4 mb-6 bg-white p-4 rounded-lg border border-gray-100 shadow-[0px_2px_4px_rgba(0,0,0,0.02)]">
        {[
          { label: 'Total', value: taskSummary.total, className: 'border-gray-200 bg-white' },
          { label: taskStatus.completed.label, value: taskSummary.completed, className: taskStatus.completed.className, textColor: 'text-gray-500' },
          { label: taskStatus.in_progress.label, value: taskSummary.inProgress, className: taskStatus.in_progress.className, textColor: 'text-gray-500' },
          { label: taskStatus.on_hold.label, value: taskSummary.onHold, className: taskStatus.on_hold.className, textColor: 'text-white' },
          { label: taskStatus.blocked.label, value: taskSummary.blocked, className: taskStatus.blocked.className, textColor: 'text-gray-500' },
          { label: taskStatus.draft.label, value: taskSummary.notStarted, className: taskStatus.draft.className, textColor: 'text-gray-500' }
        ].map((card, idx) => (
          <div key={`summary-card-${idx}`} className={cn("flex-1 min-w-[140px] rounded-lg border p-3 flex justify-between items-center h-[76px]", card.className)}>
            <div className={cn("text-[13px] font-medium leading-normal text-left", card.textColor || "text-gray-500")}>{card.label}<br/>Tasks</div>
            <div className={cn("text-[26px] font-semibold", card.label === 'On-Hold' ? "text-white" : "text-gray-900")}>
              {card.value < 10 ? '0' : ''}<CountUp value={card.value} />
            </div>
          </div>
        ))}
      </div>

      <div>
        {isLoadingTaskList ? (
          <div className="flex justify-center mt-10"><Loader /></div>
        ) : taskList && taskList.length > 0 ? (
          <div className="border-1 bg-white rounded-lg mt-5 shadow-[0px_2px_4px_rgba(0,0,0,0.05)]">
            {/* Centered Legend Inside Grid */}
            <div className="flex justify-center w-full pt-4 pb-2 border-b border-gray-50">
              <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8">
                {taskLegend.map((data: any, index: number) => (
                  <div key={`task-taskLegend-${index}`} className="flex flex-row gap-2 items-center">
                    <div className={cn("h-4 w-4 rounded-[2px]", data.className)} />
                    <span className="text-[12px] text-gray-700 font-medium">{data?.label}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex flex-col w-full p-2">
              {groupedTasks.map((group, groupIdx) => (
                <div key={`group-${groupIdx}`} className="flex flex-col md:flex-row w-full border-b border-gray-100 last:border-b-0">
                  {/* Left Side: Month/Week Card */}
                  <div className="w-[140px] shrink-0 py-4 pr-3 pl-2 flex items-start">
                    <div className="border border-gray-200 rounded-md text-center py-1.5 px-3 w-full text-[12px] text-gray-500 bg-white shadow-sm">
                      {group.label}
                    </div>
                  </div>
                  
                  {/* Right Side: Tasks Grid */}
                  <div 
                    className="grid w-full flex-1 gap-x-2 gap-y-2 py-4 pl-4 min-h-[64px] content-start"
                    style={{ gridTemplateColumns: `repeat(${Math.min(38, Math.max(1, group.tasks.length))}, minmax(0, 1fr))` }}
                  >
                    {group.tasks.map((task: any, taskIdx: number) => {
                       const statusLabel = taskStatus[task?.actualStatus?.toLowerCase()]?.label;
                       let bgColor = 'bg-gray-400'; // Not Started default
                       const statusLower = statusLabel.toLowerCase();
                       const classNameFromLegend = taskLegend.find((l: any) => l.label.toLowerCase() === statusLower)?.className;
                       
                       if (classNameFromLegend) {
                         bgColor = classNameFromLegend;
                       }
                       
                       const hoverColors = getHoverCardColors(task?.actualStatus);
                       
                       return isMobile ? <TooltipProvider>
                        <Popover>
                          <PopoverTrigger className="mx-1" asChild>
                            <div 
                              className={cn("h-4 w-full rounded-t-[3px] cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-primary/50 transition-all", bgColor)} 
                              style={{ maxWidth: group.tasks.length >= 15 ? '25px' : '100%' }}
                             />
                         </PopoverTrigger>
                         <PopoverContent 
                           className={cn("w-[240px] p-3 shadow-md rounded-xl border", hoverColors.cardBg, hoverColors.cardBorder)}>
                            <TaskTooltipContent task={task} onTaskClick={onTaskClick}/>
                         </PopoverContent>
                       </Popover>
                     </TooltipProvider> : (
                         <HoverCard key={`task-${taskIdx}`}>
                           <HoverCardTrigger asChild>
                             <div 
                               className={cn("h-4 w-full rounded-t-[3px] cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-primary/50 transition-all", bgColor)} 
                               style={{ maxWidth: group.tasks.length >= 15 ? '25px' : '100%' }}
                             />
                           </HoverCardTrigger>
                           <HoverCardContent className={cn("w-[240px] p-3 shadow-md rounded-xl border", hoverColors.cardBg, hoverColors.cardBorder)} sideOffset={8}>
                              <TaskTooltipContent task={task} onTaskClick={onTaskClick}/>
                           </HoverCardContent>
                         </HoverCard>
                       );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center border-1 bg-white rounded-lg mt-5 p-4">
            <NoData />
          </div>
        )}
      </div>
    </Container>
  )
}

export default ViewTaskPlan;